/* eslint-disable react/no-unescaped-entities */
import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Handshake, Heart } from "lucide-react";
import { ASSETS } from "@/constants/site";

const FEATURED = [
    {
        name: "AAERTF",
        sub: "African American Equity Restoration Task Force",
        role: "Strategic Partner",
    },
    {
        name: "The HopeWell Foundation",
        sub: "Resources · Recovery · Workforce Development",
        role: "Founding Partner",
    },
    {
        name: "Knoxville Utility Board",
        sub: "E.A.P. / S.A.P. Provider Engagement",
        role: "Active Engagement",
    },
];

const CATEGORIES = [
    {
        label: "Real Estate & Construction",
        partners: [
            "Wallace Real Estate",
            "Rooted Realty Co.",
            "Master Plan Builders",
            "Montauk Solutions",
            "Dixson Owned Properties Logistics",
            "We Buy Houses (Dixson)",
            "Jim McMichael Signs & Truck Painting",
        ],
    },
    {
        label: "Hospitality & Food",
        partners: [
            "Marriott",
            "The Tennessean Personal Luxury Hotel",
            "Litton's Market · Restaurant · Bakery",
            "Hamburger Hill",
            "Smash Mouth BBQ",
            "Davis Wayne's Food · Spirits · Soul",
            "Pratt's County Store",
            "Rom Pablo's",
            "Cream of the Chop Barbershop",
            "Taste of Tay",
            "Subway",
            "Kroger",
            "Old City MedSpa",
        ],
    },
    {
        label: "Beauty, Wellness & Lifestyle",
        partners: [
            "Mousiee's Everyday Salon",
            "Just 4U Creations by Shawnta",
            "Sarah Sanders Unique Hair Styling",
            "Rob's Clean Cuts and Styles",
            "Gam's Hair Fashions",
            "Natural Beauty by Fruby",
            "Burdine Beauty",
            "Mason Massage",
            "Marc Nelson",
            "Flower Fire",
            "Lotus Program",
            "Aspire Intentional Meditation",
            "Life Is Energy / Energy Is Life",
        ],
    },
    {
        label: "Fitness, Sports & Retail",
        partners: [
            "The Lions Den Fitness Club",
            "Stonecold Fitness",
            "Crunch Fitness",
            "Dick's Sporting Goods",
            "The Home Depot",
            "Office Depot",
            "VOL Market #3",
        ],
    },
    {
        label: "Trades, Services & Auto",
        partners: [
            "King Floyd Junk Removal, LLC",
            "Bishop's Lawn Care Services",
            "Front Line Lawn Care",
            "Kings Plumbing LLC",
            "Jiffy Lube",
            "M&M Tires & Rims",
            "Smiles Professional Cleaning Specialist LLC",
        ],
    },
    {
        label: "Faith, Community & Civic",
        partners: [
            "Knox County Sheriff's Office",
            "Overcomer Believers Church",
            "Unity Mortuary",
            "the Y (YMCA)",
            "First Bank",
        ],
    },
];

const SUPPLIER_LOGOS = [
    {
        name: "Foundation Building Materials",
        logo: "https://customer-assets.emergentagent.com/job_hopewell-consulting/artifacts/yrvhk4ku_IMG_20260613_193458%20%282%29.png",
    },
    {
        name: "Mohawk",
        logo: "https://customer-assets.emergentagent.com/job_hopewell-consulting/artifacts/vo1zf4l1_IMG_20260613_193649.jpg",
    },
    {
        name: "RiteRug Flooring",
        logo: "https://customer-assets.emergentagent.com/job_hopewell-consulting/artifacts/sv57k2fi_IMG_20260613_193653.png",
    },
    {
        name: "Ferguson",
        logo: "https://customer-assets.emergentagent.com/job_hopewell-consulting/artifacts/n2cfkv48_IMG_20260613_193656.png",
    },
    {
        name: "Sherwin-Williams",
        logo: "https://customer-assets.emergentagent.com/job_hopewell-consulting/artifacts/tjkwz18j_IMG_20260613_193700.png",
    },
    {
        name: "Merritt Garage Doors",
        logo: "https://customer-assets.emergentagent.com/job_hopewell-consulting/artifacts/xweamm29_IMG_20260613_193458.png",
    },
    {
        name: "Whirlpool",
        logo: "https://customer-assets.emergentagent.com/job_hopewell-consulting/artifacts/v14domnk_IMG_20260613_193457%20%281%29.png",
    },
    {
        name: "84 Lumber",
        logo: "https://customer-assets.emergentagent.com/job_hopewell-consulting/artifacts/vkcmadjx_IMG_20260613_193457%20%281%29.jpg",
    },
    {
        name: "David's Abbey Carpet & Floors",
        logo: "https://customer-assets.emergentagent.com/job_hopewell-consulting/artifacts/oq9y3tqh_IMG_20260613_193458%20%281%29.png",
    },
    {
        name: "Creative Structures, Inc.",
        logo: "https://customer-assets.emergentagent.com/job_hopewell-consulting/artifacts/1sqhbdd0_IMG_20260613_193457%20%282%29.png",
    },
    {
        name: "D.R. Horton",
        logo: "https://customer-assets.emergentagent.com/job_hopewell-consulting/artifacts/9rxpryvv_IMG_20260613_193457.jpg",
    },
    {
        name: "Grace Presbyterian Church",
        logo: "https://customer-assets.emergentagent.com/job_hopewell-consulting/artifacts/e4oy153u_IMG_20260613_193457.png",
    },
];

export default function Collaborations() {
    return (
        <div data-testid="collaborations-page">
            {/* Hero */}
            <section className="bg-[#0A192F] text-white py-28 relative overflow-hidden">
                <div className="absolute inset-0 bg-grain opacity-10" aria-hidden />
                <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                    <span className="overline">Partnerships</span>
                    <h1 className="font-serif text-5xl sm:text-6xl font-bold mt-4 leading-tight max-w-4xl">
                        Built with{" "}
                        <span className="italic text-[#D4AF37]">Knoxville.</span>
                    </h1>
                    <p className="mt-8 text-lg text-slate-300 max-w-2xl leading-relaxed">
                        Real impact takes real partners. Below are the
                        organizations, businesses, and community leaders who
                        have stood alongside Reico Hopewell and the Hopewell
                        family of ventures.
                    </p>
                </div>
            </section>

            {/* Featured */}
            <section className="py-20 bg-slate-50">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                    <div className="grid md:grid-cols-3 gap-6">
                        {FEATURED.map((p, idx) => (
                            <div
                                key={p.name}
                                data-testid={`featured-partner-${idx}`}
                                className="exec-card p-8"
                            >
                                <Handshake className="w-5 h-5 text-[#D4AF37]" />
                                <p className="mt-5 text-[10px] uppercase tracking-[0.22em] font-bold text-[#0A192F]">
                                    {p.role}
                                </p>
                                <h3 className="font-serif text-2xl font-bold mt-2 text-[#0A192F] leading-tight">
                                    {p.name}
                                </h3>
                                <p className="mt-2 text-sm text-slate-600">
                                    {p.sub}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Sponsors logo wall (visual collage) */}
            <section className="py-20">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                    <div className="text-center mb-12 max-w-3xl mx-auto">
                        <span className="overline">Sponsors · Supporters</span>
                        <h2 className="font-serif text-4xl sm:text-5xl font-bold mt-4 text-[#0A192F] leading-tight">
                            The wall of partners.
                        </h2>
                        <p className="mt-5 text-slate-600 leading-relaxed">
                            A growing community of businesses, organizations,
                            and leaders who have stood with The HopeWell
                            Foundation, AAERTF, and Reico's work across
                            Knoxville and East Tennessee.
                        </p>
                    </div>
                    <div className="bg-white border border-slate-200 p-4 sm:p-8 rounded-sm shadow-sm">
                        <img
                            src={ASSETS.sponsorsCollage}
                            alt="Sponsors and supporters of The HopeWell Foundation, AAERTF, and Reico Hopewell's community work"
                            data-testid="sponsors-collage"
                            className="w-full h-auto"
                            loading="lazy"
                        />
                    </div>
                </div>
            </section>

            {/* Categorised partner list (text, for SEO + accessibility) */}
            <section className="py-24 bg-slate-50 border-t border-slate-200">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                    <div className="max-w-3xl mb-14">
                        <span className="overline">By Category</span>
                        <h2 className="font-serif text-4xl sm:text-5xl font-bold mt-4 text-[#0A192F] leading-tight">
                            Knoxville businesses that stand for the work.
                        </h2>
                        <p className="mt-6 text-slate-600 leading-relaxed">
                            From real estate to retail, hospitality to faith
                            communities — these partners have sponsored,
                            supported, or collaborated with The HopeWell
                            Foundation and Hopewell Consulting on programs
                            that change lives.
                        </p>
                    </div>

                    <div className="space-y-16">
                        {CATEGORIES.map((cat, ci) => (
                            <div key={cat.label} data-testid={`category-${ci}`}>
                                <div className="flex items-baseline gap-4 mb-6">
                                    <span className="num-tag">
                                        {String(ci + 1).padStart(2, "0")}
                                    </span>
                                    <h3 className="font-serif text-2xl font-bold text-[#0A192F]">
                                        {cat.label}
                                    </h3>
                                    <span className="gold-rule flex-1 max-w-[60px]" />
                                </div>
                                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200">
                                    {cat.partners.map((p) => (
                                        <div
                                            key={p}
                                            className="bg-white px-5 py-6 flex items-center text-sm text-[#0A192F] font-semibold hover:bg-slate-50 transition-colors"
                                        >
                                            {p}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Add your name */}
            <section className="bg-[#0A192F] text-white py-24">
                <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
                    <Heart className="w-10 h-10 text-[#D4AF37] mx-auto mb-6" />
                    <h2 className="font-serif text-3xl sm:text-4xl font-bold leading-tight">
                        Want to add your name to this list?
                    </h2>
                    <p className="mt-5 text-slate-300 max-w-2xl mx-auto">
                        Sponsor a program. Co-host an event. Underwrite a
                        scholarship. We work with local businesses, faith
                        communities, and civic organizations who want to put
                        real weight behind real change.
                    </p>
                    <div className="mt-10 flex flex-wrap justify-center gap-4">
                        <Link
                            to="/contact?service=Community%20Partnership"
                            data-testid="collab-cta-partner"
                            className="bg-[#D4AF37] text-[#0A192F] hover:bg-[#C5A059] rounded-sm px-8 py-4 font-bold text-sm uppercase tracking-widest inline-flex items-center gap-2"
                        >
                            Become a Partner <ArrowRight className="w-4 h-4" />
                        </Link>
                        <Link
                            to="/impact"
                            className="border border-white/40 hover:bg-white/10 rounded-sm px-8 py-4 font-semibold text-sm uppercase tracking-widest"
                        >
                            See the impact
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
